/**
 * ProfileCard Component
 *
 * Displays a single company profile with:
 * - Profile name and domain
 * - Active status badge
 * - Key profile details
 * - Action buttons (Activate, Edit, Delete)
 *
 * Optimized with React.memo to prevent unnecessary re-renders
 */

import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Edit, Trash2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CompanyProfile } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
interface ProfileCardProps {
  profile: CompanyProfile;
  isActive: boolean;
  onActivate?: (profileId: string) => void;
  onEdit?: (profileId: string) => void;
  onDelete?: (profileId: string) => void;
}

const ProfileCardComponent = ({
  profile,
  isActive,
  onActivate,
  onEdit,
  onDelete,
}: ProfileCardProps): JSX.Element => {
  const navigate = useNavigate();

  const handleActivate = () => {
    if (onActivate && !isActive) {
      onActivate(profile.id);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(profile.id);
    } else {
      navigate(`/app/company-profile/edit/${profile.id}`);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(profile.id);
    }
  };

  return (
    <div
      className={`bg-card rounded-lg border transition-all hover:shadow-lg ${
        isActive ? 'ring-2 ring-primary shadow-md' : ''
      }`}
    >
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Profile Color Indicator */}
            <div
              className="h-12 w-1 rounded-full flex-shrink-0 mt-1"
              style={{ backgroundColor: profile.profile_color }}
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold truncate">
                {profile.profile_name}
              </h3>
              <p className="text-sm text-muted-foreground truncate">
                {profile.primary_domain}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            {isActive && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                Aktywny
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!isActive && (
                  <>
                    <DropdownMenuItem onClick={handleActivate}>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Aktywuj profil
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleEdit}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edytuj profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleDelete}
                  className="text-red-600 focus:text-red-600 dark:text-red-400"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Usuń profil
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Branża:</span>
            <span className="font-medium">{profile.industry}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Rynek:</span>
            <span className="font-medium">{profile.target_market}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Strategia:</span>
            <span className="font-medium capitalize">
              {profile.domain_strategy?.replace('_', ' ')}
            </span>
          </div>
        </div>

        {/* Quick Stats (if available) */}
        {(profile.gsc_monthly_clicks != null ||
          profile.gsc_total_keywords != null ||
          profile.ga4_monthly_users != null) && (
          <div className="pt-4 border-t">
            <div className="grid grid-cols-3 gap-2 text-center">
              {profile.gsc_monthly_clicks != null && (
                <div>
                  <div className="text-xs text-muted-foreground">Kliknięcia</div>
                  <div className="text-sm font-semibold">
                    {profile.gsc_monthly_clicks.toLocaleString()}
                  </div>
                </div>
              )}
              {profile.gsc_total_keywords != null && (
                <div>
                  <div className="text-xs text-muted-foreground">Słowa kluczowe</div>
                  <div className="text-sm font-semibold">
                    {profile.gsc_total_keywords.toLocaleString()}
                  </div>
                </div>
              )}
              {profile.ga4_monthly_users != null && (
                <div>
                  <div className="text-xs text-muted-foreground">Użytkownicy</div>
                  <div className="text-sm font-semibold">
                    {profile.ga4_monthly_users.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Primary Action */}
        <div className="pt-4 border-t">
          {isActive ? (
            <Button
              onClick={() => navigate('/app/company-profile')}
              className="w-full"
              variant="default"
            >
              Zobacz panel
            </Button>
          ) : (
            <Button
              onClick={handleActivate}
              className="w-full"
              variant="outline"
            >
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Aktywuj i zobacz
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Export memoized component to prevent unnecessary re-renders
export const ProfileCard = memo(ProfileCardComponent);
